import SectionHeader from "../../../../util/component/SectionHeader";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {ColDef, ICellRendererParams, IServerSideDatasource, RowClickedEvent} from "ag-grid-community";
import {getContractorsRoyalties} from "../service/contractorSearch.actions";
import ContentBody from "../../common/content-body/ContentBody";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton} from "@mui/material";
import RoyaltiesVoices from "./RoyaltiesVoices";
import {DescriptionRounded, Preview} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useHasScope} from "../../../../config/globalConfig";
import { NetworkScope } from "../../types";
import { useRecoilState } from "recoil";
import { royaltiesFilterParams } from "../model/state";
import { deepClone } from "../../../../util/state/commonFunctions";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import RoyaltiesFilter from "./RoyaltiesFilter";
import { IRoyaltiesSearchFilters } from "../model/model";
import { useForm } from "react-hook-form";
import { filterObject } from "../../utils";
import { ROYALTIES_SEARCH_FILTERS_FORM_INITIAL_VALUES } from "../utils/config";

const RoyaltiesConfigurationPanel = () => {
    const hasScope = useHasScope;
    const {t} = useTranslation(['network', 'translation']);
    const [open, setOpen] = useState(false);
    const handleClose = () => {
        setOpen(false);
    }
    const navigate = useNavigate();
    const [contractor, setContractor] = useState({});
    const [isSearching, setIsSearching] = useState(false);
    const gridRef = useRef<AgGridReact>(null);
    const columnDefs = useMemo<ColDef[]>(() => [
        {
            field: "test",
            headerName: "",
            suppressSizeToFit: false,
            width: 70,
            pinned: "right",
            cellStyle: {
                'alignText': 'center',
                'justifyContent': "flex-center"
            },
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    hasScope( NetworkScope.MsDetail) &&
                    <IconButton color={"accent"} onClick={() => routeChange(params.data)}>
                        <DescriptionRounded />
                    </IconButton>
                );
            }
        },
        {field: 'contractorIdentificationCode', headerName: t("contractorMng.royaltiesConfigurationPanel.grid.contractorId")},
        {field: 'contractorName', headerName: t("contractorMng.royaltiesConfigurationPanel.grid.contractorName")},
        {field: 'contractorType', headerName: t("contractorMng.royaltiesConfigurationPanel.grid.contractorType")}
    ], []);

    const {globalData} = useContext(GlobalContext);
    const [filterParams, setFilterParams] = useRecoilState(royaltiesFilterParams);
    let filterParamsClone = deepClone(filterParams);

    const defaultColDef = useMemo(() => {
        return {
            lockPinned: true,
            resizable: true,
        }
    }, []);

    const routeChange = (contractor: any) => {
        let path = `/mobility-store/contractor-royalties-detail`;
        navigate(path, {state:{
                contractor: contractor
            }});
    }

    const searchRoyaltiesForm = useForm<IRoyaltiesSearchFilters>({
        defaultValues: ROYALTIES_SEARCH_FILTERS_FORM_INITIAL_VALUES
    });

    const serverSideDatasource: IServerSideDatasource = useMemo(() => ({
        getRows(params) {
            if (!filterParamsClone) {
                filterParamsClone = {};
            }

            filterParamsClone['paramsRequest'] = {
                pagination: Number(params.request.endRow) - Number(params.request.startRow),
                startIndex: params.request.startRow,
                endIndex: params.request.endRow
            };

            if (!!filterParamsClone.tenants) {
                const ind = filterParamsClone.tenants.findIndex((elem: any) => elem.name === 'all');
                if (ind > -1) {
                    filterParamsClone.tenants =
                        globalData.user.legalEntities
                            .filter((elem: any) => elem.name !== 'all')
                            .map((elem: any) => elem.name);
                } else {
                    filterParamsClone.tenants = filterParamsClone.tenants.map((elem: any) => elem.name);
                }
            }

            getContractorsRoyalties(filterParamsClone)
                .then((response: any) => {
                    params.success({
                        rowData: response.contractors,
                        rowCount: response.params.totCount
                    })
                    setIsSearching(false);
                })
                .catch(() => {
                    params.fail();
                    setIsSearching(false);
                })
                .finally(() => {
                    setIsSearching(false);
                })
        }
    }), [isSearching, filterParams]);

    useEffect(() => {
        if (globalData.user.legalEntities.length === 1) {
            searchRoyaltiesForm.setValue('tenants', globalData.user.legalEntities);
        } else if (globalData.user.legalEntities.length > 1) {
            searchRoyaltiesForm.setValue('tenants', globalData.user.legalEntities.filter((elem: any) => elem.name !== 'all'));
            searchRoyaltiesForm.setValue('dealers', globalData.user.dealers.filter((elem: any) => elem.name !== 'all'));
        }

        const data = searchRoyaltiesForm.getValues();
        setFilterParams(filterObject(data, x => JSON.stringify(data[x]) !== JSON.stringify(ROYALTIES_SEARCH_FILTERS_FORM_INITIAL_VALUES[x])));
    }, []);

    const handleSearch = (data: any) => {
        setFilterParams(filterObject(data, x => JSON.stringify(data[x]) !== JSON.stringify(ROYALTIES_SEARCH_FILTERS_FORM_INITIAL_VALUES[x])));
    };

    const handleRefresh = () => {
        gridRef.current!.api.refreshServerSide({ route: undefined, purge: true })
        handleClose();
    }

    return(
        <>
            <SectionHeader title={t("contractorMng.royaltiesConfigurationPanel.title")}/>
            <ContentBody >
                {globalData.user.legalEntities.length > 1 && <RoyaltiesFilter
                    handleSearch={handleSearch}
                    searchRoyaltiesForm={searchRoyaltiesForm}
                />}
                <AgGridCustomTable
                    gridRef={gridRef}
                    // rowData={tableData}
                    columnDefs={columnDefs}
                    rowModelType={'serverSide'}
                    serverSideInfiniteScroll={true}
                    serverSideDatasource={serverSideDatasource}
                    gridOptions={
                        {
                            defaultColDef: defaultColDef,
                            onGridReady: (event) => {
                                event.api.sizeColumnsToFit()
                            }
                        }
                    }
                />
            </ContentBody>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    {t("contractorMng.royaltiesConfigurationPanel.title")}
                </DialogTitle>
                <DialogContent>
                    <RoyaltiesVoices contractorData={contractor} handleCloseRefresh={handleRefresh} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t("contractorMng.royaltiesConfigurationPanel.dialog.action.cancel")}</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default RoyaltiesConfigurationPanel;